<script setup>
import { ref, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { dynamicRoutes } from '@/router/setupRouterWithAuth';
import { CloseOutlined } from '@ant-design/icons-vue';

const router = useRouter();
const route = useRoute();

// 儲存頁籤的資料：
// key 使用路由 name（若無 name 才回退 path），避免同一型別路由（如 /members/:id）開多個分頁
// 每個分頁額外保存當前 path 用於跳轉
const tabs = ref([
    {
        key: 'Dashboard',
        title: '控制台',
        path: '/dashboard',
        closable: false
    }
]);

const activeKey = ref('');

// 遞迴查找路由配置中的路徑對應的標題
const findRouteTitle = (path, routesList = null) => {
    // 移除開頭和結尾的斜線
    const normalizePath = (p) => p.replace(/^\/+|\/+$/g, '');
    const targetPath = normalizePath(path);

    const findTitle = (routes, parentPath = '') => {
        for (const route of routes) {
            const currentPath = parentPath
                ? `${normalizePath(parentPath)}/${normalizePath(route.path)}`
                : normalizePath(route.path);

            // 檢查當前路由
            if (currentPath === targetPath && route.meta?.title) {
                return route.meta.title;
            }

            // 檢查子路由
            if (route.children) {
                const result = findTitle(route.children, currentPath);
                if (result) return result;
            }
        }
        return null;
    };

    // 優先從當前路由配置中查找
    const currentRoutes = router.getRoutes();
    for (const route of currentRoutes) {
        if (route.path === '/') {
            // 對於根路由，直接搜索其子路由
            const result = findTitle(route.children || []);
            if (result) return result;
        } else {
            // 對於其他頂層路由
            const result = findTitle([route]);
            if (result) return result;
        }
    }

    // 如果當前路由配置中找不到，再從 dynamicRoutes 中查找
    if (routesList) {
        for (const route of routesList) {
            if (route.path === '/') {
                // 對於根路由，直接搜索其子路由
                const result = findTitle(route.children || []);
                if (result) return result;
            } else {
                // 對於其他頂層路由
                const result = findTitle([route]);
                if (result) return result;
            }
        }
    }

    return '未知页面';
};

// 修改 getTabTitle：優先從 matched 取得，支援動態路由；再回退遞迴查找
const getTabTitle = (path) => {
    // 1) 針對當前路由（watch 觸發時 newPath === route.path）
    if (path === route.path && Array.isArray(route.matched)) {
        const last = route.matched[route.matched.length - 1];
        if (last?.meta?.title) return last.meta.title;
    }

    // 2) 使用 router.resolve 支援帶參的 path（/members/123 對應 /members/:id）
    const resolved = router.resolve(path);
    if (resolved && Array.isArray(resolved.matched)) {
        const last = resolved.matched[resolved.matched.length - 1];
        if (last?.meta?.title) return last.meta.title;
    }

    // 3) 回退：從路由樹遞迴比對（原邏輯）
    return findRouteTitle(path);
};

// 取得 Tab 唯一鍵：優先使用路由 name，若沒有則回退 path
const getTabKey = (path) => {
    const resolved = router.resolve(path);
    const last = resolved?.matched?.[resolved.matched.length - 1];
    return last?.name || path;
};

// 監聽路由變化
watch(
    () => route.path,
    (newPath) => {
        const key = getTabKey(newPath);
        activeKey.value = key;

        const existingTab = tabs.value.find((tab) => tab.key === key);
        if (existingTab) {
            // 更新現有分頁的顯示資訊與當前路徑
            existingTab.title = getTabTitle(newPath);
            existingTab.path = newPath;
        } else {
            // 新增頁籤（Dashboard 不可關閉：依 name 或 path 判斷）
            const closable = key !== 'Dashboard' && newPath !== '/dashboard';
            tabs.value.push({
                key,
                title: getTabTitle(newPath),
                path: newPath,
                closable
            });
        }
    },
    { immediate: true }
);

// 處理頁籤切換
const handleTabChange = (key) => {
    const target = tabs.value.find((t) => t.key === key);
    router.push(target?.path || key);
};

// 處理頁籤關閉
const handleTabEdit = (targetKey, action) => {
    if (action === 'remove') {
        // 找出要關閉的頁籤索引
        const targetIndex = tabs.value.findIndex(
            (tab) => tab.key === targetKey
        );

        // 如果關閉的是當前激活的頁籤，需要切換到其他頁籤
        if (targetKey === activeKey.value) {
            // 優先切換到左邊的頁籤，如果沒有左邊的就切換到右邊的
            const newTab =
                tabs.value[targetIndex - 1] || tabs.value[targetIndex + 1];

            // 如果找到其他標籤，跳轉到該標籤的 path
            // 如果沒有其他標籤（只剩 Dashboard 或沒有標籤），跳轉到 Dashboard
            if (newTab && newTab.path) {
                router.push(newTab.path);
            } else {
                // 預設跳轉到 Dashboard
                router.push('/dashboard');
            }
        }

        // 移除頁籤
        tabs.value = tabs.value.filter((tab) => tab.key !== targetKey);
    }
};
</script>

<template>
    <a-tabs
        v-model:activeKey="activeKey"
        type="editable-card"
        hide-add
        @change="handleTabChange"
        @edit="handleTabEdit"
        class="vaporwave-tabs-nav"
    >
        <a-tab-pane
            v-for="tab in tabs"
            :key="tab.key"
            :tab="tab.title"
            :closable="tab.closable"
        ></a-tab-pane>
    </a-tabs>
</template>

<style scoped>
/* 蒸氣波風格頁籤導航 - 保持原本尺寸和邏輯 */
.vaporwave-tabs-nav {
    background: transparent;
    margin-bottom: 0;
}

:deep(.ant-tabs-nav) {
    margin-bottom: 0 !important;
    padding: 0 8px;
    min-height: 36px !important;
    background: transparent !important;
}

:deep(.ant-tabs-tab) {
    padding: 4px 12px !important;
    margin: 0 2px !important;
    font-size: 13px !important;
    border: 1px solid rgba(42, 74, 122, 0.4) !important;
    border-radius: 4px !important;
    background: rgba(255, 255, 255, 0.9) !important;
    transition: all 0.3s ease !important;
    color: #4a4a5a !important;
}

:deep(.ant-tabs-tab:hover) {
    border-color: #2a4a7a !important;
    color: #2a2a3a !important;
    background: rgba(42, 74, 122, 0.1) !important;
    box-shadow: 0 0 8px rgba(42, 74, 122, 0.2) !important;
}

:deep(.ant-tabs-tab-active) {
    background: rgba(42, 74, 122, 0.15) !important;
    border-color: #2a4a7a !important;
    color: #2a2a3a !important;
    box-shadow:
        0 0 12px rgba(42, 74, 122, 0.3),
        inset 0 0 8px rgba(42, 74, 122, 0.1) !important;
}

:deep(.ant-tabs-nav-wrap) {
    padding: 4px 0 !important;
}

:deep(.anticon) {
    font-size: 12px !important;
    color: #8a7a9a !important;
    transition: all 0.3s ease !important;
}

:deep(.ant-tabs-tab:hover .anticon) {
    color: #2a4a7a !important;
}

:deep(.ant-tabs-tab-active .anticon) {
    color: #2a4a7a !important;
}

:deep(.ant-tabs-tab-remove) {
    margin-left: 4px !important;
    padding: 0 !important;
}

:deep(.ant-tabs-nav::before) {
    border-bottom: none !important;
}

:deep(.ant-tabs-nav-list) {
    border: none !important;
}

:deep(.ant-tabs-tab-btn) {
    color: inherit !important;
    transition: all 0.3s ease !important;
}

:deep(.ant-tabs-tab-active .ant-tabs-tab-btn) {
    color: #2a2a3a !important;
    font-weight: 600 !important;
    text-shadow: none !important;
}
</style>
